import { isDueForSending, type PositionSample, type TourSettings } from '@konvoi/shared';

/**
 * Die Positionsermittlung im Browser.
 *
 * Sie hat drei Eigenheiten, die den ganzen Zuschnitt bestimmen:
 *
 *   * Sie arbeitet nur im gesicherten Kontext — deshalb HTTPS überall, auch
 *     lokal.
 *   * Sie endet, sobald die Seite in den Hintergrund gerät oder das Gerät
 *     gesperrt wird. Das ist nicht umgehbar; behandelt wird es an anderer
 *     Stelle, indem der Zustand gemeldet und angezeigt wird.
 *   * Sie liefert gelegentlich Unsinn. Was zu ungenau ist, geht gar nicht erst
 *     hinaus.
 */
export interface TrackerCallbacks {
  /** Eine Messung, die gesendet werden soll. */
  onSample: (sample: PositionSample) => void;
  /** Eine Messung wurde verworfen — für die Anzeige „Empfang schlecht". */
  onRejected?: (reason: string) => void;
  onError?: (message: string) => void;
}

export class PositionTracker {
  #watchId: number | null = null;
  #lastSent: PositionSample | null = null;
  #settings: TourSettings;

  constructor(
    settings: TourSettings,
    private readonly callbacks: TrackerCallbacks,
  ) {
    this.#settings = settings;
  }

  updateSettings(settings: TourSettings): void {
    this.#settings = settings;
  }

  get running(): boolean {
    return this.#watchId !== null;
  }

  start(): void {
    if (this.#watchId !== null) {
      return;
    }

    if (!('geolocation' in navigator)) {
      this.callbacks.onError?.('Dieses Gerät kann den Standort nicht ermitteln.');
      return;
    }

    this.#watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.handle(position);
      },
      (error) => {
        this.callbacks.onError?.(this.describeError(error));
      },
      {
        // Ohne hohe Genauigkeit liefern Geräte die Funkzellenposition — auf
        // einer Landstraße wären das Hunderte Meter daneben.
        enableHighAccuracy: true,
        // Keine zwischengespeicherten Messungen: Eine alte Position ist hier
        // schlimmer als keine.
        maximumAge: 0,
        timeout: 10_000,
      },
    );
  }

  stop(): void {
    if (this.#watchId !== null) {
      navigator.geolocation.clearWatch(this.#watchId);
      this.#watchId = null;
    }
  }

  private handle(position: GeolocationPosition): void {
    const sample: PositionSample = {
      lat: position.coords.latitude,
      lon: position.coords.longitude,
      recordedAt: Math.round(position.timestamp),
      accuracyM: position.coords.accuracy,
      ...(position.coords.speed === null ? {} : { speedMps: Math.max(0, position.coords.speed) }),
      ...(position.coords.heading === null || Number.isNaN(position.coords.heading)
        ? {}
        : { headingDeg: ((position.coords.heading % 360) + 360) % 360 }),
      ...(position.coords.altitude === null ? {} : { altitudeM: position.coords.altitude }),
    };

    if (sample.accuracyM > this.#settings.minPositionAccuracyM) {
      this.callbacks.onRejected?.(`Messung auf ${Math.round(sample.accuracyM)} m genau — verworfen`);
      return;
    }

    if (!isDueForSending(this.#lastSent, sample)) {
      return;
    }

    this.#lastSent = sample;
    this.callbacks.onSample(sample);
  }

  private describeError(error: GeolocationPositionError): string {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'Die Standortfreigabe fehlt. Ohne sie sehen dich die anderen nicht.';
      case error.POSITION_UNAVAILABLE:
        return 'Kein Standort verfügbar. Vermutlich kein Empfang.';
      case error.TIMEOUT:
        return 'Der Standort kam nicht rechtzeitig.';
      default:
        return 'Der Standort lässt sich gerade nicht ermitteln.';
    }
  }
}
